/**
 * Ejecución de la entrevista.
 *
 * Garantías que esta pantalla debe cumplir:
 *
 *  - NO SE PIERDE NADA. Cada respuesta se escribe en IndexedDB de inmediato. Si
 *    la app se cierra, el teléfono se apaga o el navegador descarta la
 *    pestaña, la entrevista se retoma donde quedó.
 *  - EL ORDEN ES EL DEL INSTRUMENTO. No se puede saltar hacia adelante; sólo
 *    retroceder para corregir. §7 ordena los bloques para que ninguna pregunta
 *    "enseñe" un nombre antes de medirlo sin ayuda, y permitir navegación libre
 *    rompería ese diseño.
 *  - LOS CORTES SE RESPETAN. F2 = 0, F4 = no y F6 = no acepta terminan la
 *    entrevista con su disposición, no la dejan continuar.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { QuestionHeader, QuestionInput } from '../components/QuestionInput';
import { api } from '../api/client';
import { db, type DraftAnswer, type DraftInterview } from '../db/db';
import { enqueue, syncNow } from '../db/queue';
import {
  buildSteps,
  checkTermination,
  computeProgress,
  knownPersonajes,
  personajeAwareness,
  toApiAnswers,
  validateStep,
  type EngineContext,
  type Step,
} from '../engine/runner';
import { useApp } from '../store';

export function InterviewScreen() {
  const { clientUuid } = useParams<{ clientUuid: string }>();
  const navigate = useNavigate();
  const { fieldPackage, markSynced, refreshPending } = useApp();

  const [draft, setDraft] = useState<DraftInterview | null>(null);
  const [index, setIndex] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [termination, setTermination] = useState<{ disposition: string; message: string } | null>(
    null,
  );
  const [finishing, setFinishing] = useState(false);
  const [i6Requested, setI6Requested] = useState(false);

  /* ------------------------------------------------------------------ */
  /*                        Carga del borrador                          */
  /* ------------------------------------------------------------------ */

  useEffect(() => {
    if (!clientUuid) return;
    let cancelled = false;
    void (async () => {
      const found = await db.drafts.get(clientUuid);
      if (cancelled) return;
      if (!found) {
        navigate('/', { replace: true });
        return;
      }
      setDraft(found);
    })();
    return () => {
      cancelled = true;
    };
  }, [clientUuid, navigate]);

  /* ------------------------------------------------------------------ */
  /*                      Secuencia de pantallas                        */
  /* ------------------------------------------------------------------ */

  const ctx: EngineContext | null = useMemo(
    () => (fieldPackage && draft ? { pkg: fieldPackage, draft } : null),
    [fieldPackage, draft],
  );

  // La secuencia se recalcula con cada respuesta: la lógica condicional puede
  // añadir o quitar pantallas (P2 contra P3, I2-I5 según los conocidos, X1
  // según la celda), así que no se puede fijar al abrir.
  const steps: Step[] = useMemo(() => (ctx ? buildSteps(ctx) : []), [ctx]);

  // Al retomar un borrador se coloca el cursor en la pregunta guardada.
  const cursorRestored = useRef(false);
  useEffect(() => {
    if (cursorRestored.current || !draft || steps.length === 0) return;
    cursorRestored.current = true;
    if (draft.cursor) {
      const position = steps.findIndex((s) => s.id === draft.cursor);
      if (position >= 0) setIndex(position);
    }
  }, [draft, steps]);

  const step = steps[Math.min(index, Math.max(0, steps.length - 1))];

  /* ------------------------------------------------------------------ */
  /*                      Persistencia inmediata                        */
  /* ------------------------------------------------------------------ */

  const persist = useCallback(
    async (patch: Partial<DraftInterview>) => {
      if (!clientUuid) return;
      setDraft((previous) => {
        if (!previous) return previous;
        const next = { ...previous, ...patch, updatedAt: Date.now() };
        // Escritura sin await en el render: el borrador en memoria ya refleja el
        // cambio y la escritura es de milisegundos. Si fallara, el siguiente
        // toque la reintenta.
        void db.drafts.put(next);
        return next;
      });
    },
    [clientUuid],
  );

  const handleAnswer = useCallback(
    (key: string, answer: DraftAnswer | null) => {
      setError(null);
      setDraft((previous) => {
        if (!previous) return previous;
        const answers = { ...previous.answers };
        if (answer === null) delete answers[key];
        else answers[key] = answer;
        const next = { ...previous, answers, updatedAt: Date.now() };
        void db.drafts.put(next);
        return next;
      });
    },
    [],
  );

  /* ------------------------------------------------------------------ */
  /*                      Respuestas prerrellenadas                     */
  /* ------------------------------------------------------------------ */

  /**
   * Escribe el valor por omisión al llegar a la pregunta.
   *
   * Se guarda como respuesta de verdad y no como marcador de posición: si
   * sólo se pintara en gris, el encuestador que no toca el campo dejaría la
   * respuesta vacía y la pregunta aparecería sin contestar en la exportación,
   * que es justo lo contrario de lo que se busca al poner un valor por
   * omisión.
   *
   * Sólo se escribe si NO hay respuesta previa. Una vez que el encuestador
   * escribe algo —o lo borra a propósito— esto no vuelve a tocarlo, porque
   * pisarle lo que acaba de teclear al volver atrás sería peor que no
   * rellenar nada.
   */
  useEffect(() => {
    if (!step || !draft) return;
    const existente = draft.answers[step.answerKey];
    if (existente) return;

    const question = step.question;
    let texto: string | null = null;

    if (question.prefillFrom === 'gps-location') {
      /**
       * Se prefiere lo DETECTADO sobre lo asignado.
       *
       * La asignación dice dónde debería estar el encuestador; el GPS dice
       * dónde está. Cuando difieren —y §9.4 existe porque difieren— copiar la
       * asignación escribiría en el expediente un lugar en el que nadie
       * estuvo. Si no hay detección se cae a la asignación, señalándolo.
       */
      const deteccion = draft.gpsDetection as
        | { detectedFrameSeccion?: number | null; detectedColonia?: string | null }
        | null;
      const seccion = deteccion?.detectedFrameSeccion ?? null;
      const colonia = deteccion?.detectedColonia ?? draft.coloniaDeclared ?? null;
      const partes: string[] = [];
      if (colonia) partes.push(colonia);
      if (seccion !== null) partes.push(`Sección ${seccion}`);
      texto = partes.length > 0 ? partes.join(' · ') : null;
    } else if (question.defaultText) {
      texto = question.defaultText;
    }

    if (texto) {
      handleAnswer(step.answerKey, {
        questionCode: question.code,
        // F3 y F5 no son baterías; la fila no aplica.
        rowCode: null,
        textValue: texto,
      });
    }
  }, [step, draft, handleAnswer]);

  /* ------------------------------------------------------------------ */
  /*                   Asignación de I6 al cerrar I1                    */
  /* ------------------------------------------------------------------ */

  /**
   * I6 recae sobre 2 personajes CONOCIDOS, priorizando los de menor cobertura
   * acumulada. Esa prioridad sólo la puede calcular el servidor, que ve toda la
   * muestra; por eso se pide en cuanto I1 está completa y antes de llegar a I6.
   */
  useEffect(() => {
    if (!ctx || !draft) return;

    const i1Complete = draft.personajeOrder.every(
      (id) => draft.answers[`I1::${id}`]?.optionCode !== undefined,
    );
    if (!i1Complete) return;

    const known = new Set(knownPersonajes(ctx));
    /**
     * Si una corrección de I1 dejó inválido a algún asignado, hay que volver
     * a pedir la asignación aunque ya se hubiera hecho.
     *
     * El runner ya evita PREGUNTAR por un desconocido, pero sin esto la
     * submuestra se quedaría con un personaje menos: lo que se busca es
     * sustituirlo, no perderlo, porque I6 va a 2 personajes por entrevistado
     * y la cobertura de §10.1.4 se reparte sobre ese supuesto.
     */
    const asignacionInvalida =
      draft.i6PersonajeIds.length > 0 && draft.i6PersonajeIds.some((id) => !known.has(id));

    if (draft.i6PersonajeIds.length > 0 && !asignacionInvalida) return;
    if (i6Requested && !asignacionInvalida) return;

    setI6Requested(true);
    void (async () => {
      const lista = [...known];
      if (lista.length === 0) {
        // Dejó de conocer a todos: I6 no aplica y la asignación previa sobra.
        if (draft.i6PersonajeIds.length > 0) await persist({ i6PersonajeIds: [] });
        return;
      }
      try {
        const result = await api.assignI6(draft.clientUuid, lista, personajeAwareness(ctx));
        await persist({ i6PersonajeIds: result.i6PersonajeIds });
      } catch {
        // Sin red: se resuelve en el dispositivo tomando los primeros del orden
        // rotado. No replica la priorización por cobertura —imposible sin ver
        // la muestra completa— y queda registrado que fue una asignación local.
        const trim = ctx.pkg.trims.find((t) => t.key === 'i6-single-personaje');
        const size = trim?.enabled ? (trim.assignmentSize ?? 1) : 2;
        // Sin red tampoco se mezclan niveles: primero los que conoce bien.
        const awareness = personajeAwareness(ctx);
        const porConocimiento = [...lista].sort(
          (a, b) =>
            (awareness[a] === 'conoce_bien' ? 0 : 1) - (awareness[b] === 'conoce_bien' ? 0 : 1),
        );
        await persist({ i6PersonajeIds: porConocimiento.slice(0, size) });
      }
    })();
  }, [ctx, draft, i6Requested, persist]);

  /* ------------------------------------------------------------------ */
  /*                             Navegación                             */
  /* ------------------------------------------------------------------ */

  const goNext = useCallback(() => {
    if (!step || !ctx || !draft) return;

    const validation = validateStep(step, draft.answers);
    if (!validation.valid) {
      setError(validation.message ?? 'Revisa la respuesta.');
      return;
    }

    // Cortes de elegibilidad y consentimiento.
    const stop = checkTermination(step.question, ctx);
    if (stop) {
      setTermination({ disposition: stop.disposition, message: stop.message });
      return;
    }

    setError(null);
    const nextIndex = index + 1;
    if (nextIndex >= steps.length) {
      void finish();
      return;
    }
    setIndex(nextIndex);
    void persist({ cursor: steps[nextIndex]!.id });
  }, [step, ctx, draft, index, steps, persist]);

  const goBack = useCallback(() => {
    setError(null);
    if (index === 0) return;
    const previousIndex = index - 1;
    setIndex(previousIndex);
    void persist({ cursor: steps[previousIndex]!.id });
  }, [index, steps, persist]);

  /* ------------------------------------------------------------------ */
  /*                              Cierre                                */
  /* ------------------------------------------------------------------ */

  const finish = useCallback(
    async (terminatedAs?: string) => {
      if (!draft || finishing) return;
      setFinishing(true);
      try {
        const finishedAt = new Date().toISOString();
        const status = terminatedAs ? 'parcial' : 'completada';

        const payload = {
          clientUuid: draft.clientUuid,
          assignmentId: draft.assignmentId,
          status,
          isPilot: draft.isPilot,
          startedAt: draft.startedAt,
          finishedAt,
          gps: draft.gps,
          coloniaDeclared: draft.coloniaDeclared,
          outOfPolygonReason: draft.outOfPolygonReason,
          outOfPolygonNote: draft.outOfPolygonNote,
          gpsEvaluatedOffline: Boolean(
            (draft.gpsDetection as { evaluatedOffline?: boolean } | null)?.evaluatedOffline,
          ),
          personajeOrder: draft.personajeOrder,
          partyOrder: draft.partyOrder,
          experimentCellId: draft.experimentCellId,
          experimentCellIneligible: isExperimentCellIneligible(draft),
          i6PersonajeIds: draft.i6PersonajeIds,
          trimState: draft.trimState,
          thirdPartyPresent: draft.thirdPartyPresent,
          observations: draft.observations,
          answers: toApiAnswers(draft),
          pii:
            draft.contactName || draft.contactPhone
              ? { name: draft.contactName, phone: draft.contactPhone }
              : null,
          household: draft.householdClientUuid
            ? await buildHouseholdPayload(draft, terminatedAs)
            : null,
        };

        const sectionLabel = draft.expectedFrameSeccion ?? '?';
        await enqueue(
          draft.clientUuid,
          'interview',
          payload,
          `Sección ${sectionLabel} · ${status === 'completada' ? 'completa' : 'parcial'}`,
        );

        // El borrador se marca terminado; la cola lo borra al confirmarse el
        // envío. Borrarlo aquí perdería la entrevista si el envío fallara.
        await persist({ finishedAt, terminatedAs: terminatedAs ?? null });
        await refreshPending();

        void syncNow().then(() => markSynced());
        navigate(`/asignacion/${draft.assignmentId}?enviada=1`, { replace: true });
      } finally {
        setFinishing(false);
      }
    },
    [draft, finishing, persist, refreshPending, markSynced, navigate],
  );

  /* ------------------------------------------------------------------ */
  /*                              Render                                */
  /* ------------------------------------------------------------------ */

  if (!fieldPackage) {
    return (
      <div className="center-screen">
        <p className="muted">No hay instrumento descargado. Conéctate para obtenerlo.</p>
      </div>
    );
  }

  if (!draft || !step || !ctx) {
    return (
      <div className="center-screen">
        <div className="spinner" />
      </div>
    );
  }

  if (termination) {
    return (
      <TerminationScreen
        termination={termination}
        onConfirm={() => void finish(termination.disposition)}
        busy={finishing}
      />
    );
  }

  const progress = computeProgress(
    steps,
    index,
    draft.startedAt,
    fieldPackage.definition.estimatedMinutes,
  );
  const codebook = step.question.codebookId
    ? fieldPackage.codebooks.find((c) => c.id === step.question.codebookId)
    : undefined;
  const isLast = index === steps.length - 1;

  return (
    <div className="app">
      <div className="topbar">
        <div style={{ flex: 1 }}>
          <div className="progress">
            <div className="progress__bar" style={{ width: `${progress.share * 100}%` }} />
          </div>
          <div className="progress__meta">
            <span>
              {index + 1} / {steps.length}
            </span>
            <span className={paceClass(progress.elapsedMinutes, progress.designMinutes)}>
              {Math.round(progress.elapsedMinutes)} min
            </span>
          </div>
        </div>
      </div>

      <main className="app__main">
        <QuestionHeader step={step} />

        {/* La tarjeta de nombres se muestra en el Bloque 3 y en el 9,
            en el MISMO orden rotado, como exige el documento. */}
        {needsCardReminder(step) ? (
          <div className="notice notice--info">
            <span className="notice__title">Muestra la tarjeta</span>
            {/* La cifra sale del orden que mandó el servidor para ESTA
                entrevista, no de una constante: si el catálogo cambia, el
                aviso no puede quedar pidiendo una tarjeta de otro tamaño. */}
            Presenta la tarjeta con los {draft.personajeOrder.length} nombres en el orden de esta
            pantalla. Sin partido, sin foto y sin cargo.
          </div>
        ) : null}

        <QuestionInput
          step={step}
          answers={draft.answers}
          onAnswer={handleAnswer}
          codebook={codebook}
        />

        {/* Cierre: datos de contacto y observaciones del Bloque 12. */}
        {isLast ? <ClosingFields draft={draft} persist={persist} /> : null}

        {error ? <p className="field__error" style={{ marginTop: 14 }}>{error}</p> : null}

        {step.question.methodologyNote ? (
          <details style={{ marginTop: 20 }}>
            <summary className="faint">Nota metodológica</summary>
            <p className="faint" style={{ marginTop: 8 }}>
              {step.question.methodologyNote}
            </p>
          </details>
        ) : null}
      </main>

      <div className="actionbar">
        <button
          type="button"
          className="btn btn--secondary"
          onClick={goBack}
          disabled={index === 0 || finishing}
        >
          Atrás
        </button>
        <button
          type="button"
          className="btn btn--primary btn--grow"
          onClick={goNext}
          disabled={finishing}
        >
          {finishing ? 'Guardando…' : isLast ? 'Finalizar entrevista' : 'Siguiente'}
        </button>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*                            Campos de cierre                                */
/* -------------------------------------------------------------------------- */

/**
 * Bloque 12 · Q1, Q2 y Q4.
 *
 * El aviso sobre los datos de contacto no es decorativo: §11 los limita al
 * control de calidad y obliga a eliminarlos tras la auditoría. Decírselo al
 * entrevistado es parte del guion, y recordárselo al encuestador evita que los
 * pida como si fueran obligatorios.
 */
function ClosingFields({
  draft,
  persist,
}: {
  draft: DraftInterview;
  persist: (patch: Partial<DraftInterview>) => Promise<void>;
}) {
  return (
    <div style={{ marginTop: 24 }}>
      <div className="divider" />

      <div className="readaloud">
        Hemos terminado. Por control de calidad, un supervisor podría llamarle para confirmar que
        realicé la entrevista. ¿Me permite anotar su nombre de pila y un teléfono? Es opcional.
      </div>

      <div className="field">
        <label className="field__label" htmlFor="contact-name">
          Q1 · Nombre de pila (opcional)
        </label>
        <input
          id="contact-name"
          className="input"
          value={draft.contactName ?? ''}
          onChange={(e) => void persist({ contactName: e.target.value || null })}
          autoComplete="off"
          autoCapitalize="words"
        />
      </div>

      <div className="field">
        <label className="field__label" htmlFor="contact-phone">
          Q2 · Teléfono (opcional, sólo control de calidad)
        </label>
        <input
          id="contact-phone"
          className="input"
          type="tel"
          inputMode="tel"
          value={draft.contactPhone ?? ''}
          onChange={(e) => void persist({ contactPhone: e.target.value || null })}
          autoComplete="off"
        />
      </div>

      <p className="faint">
        Si la persona no quiere darlos, deja los campos vacíos y continúa. Se guardan cifrados, se
        usan sólo para la verificación del supervisor y se eliminan al cerrar la auditoría.
      </p>

      <div className="field" style={{ marginTop: 16 }}>
        <label className="field__label" htmlFor="observations">
          Q4 · Observaciones del encuestador
        </label>
        <textarea
          id="observations"
          className="textarea"
          value={draft.observations ?? ''}
          placeholder="Incidencias relevantes de la entrevista"
          onChange={(e) => void persist({ observations: e.target.value || null })}
        />
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*                          Pantalla de terminación                           */
/* -------------------------------------------------------------------------- */

function TerminationScreen({
  termination,
  onConfirm,
  busy,
}: {
  termination: { disposition: string; message: string };
  onConfirm: () => void;
  busy: boolean;
}) {
  return (
    <div className="app">
      <main className="app__main">
        <h1 className="screen-title">La entrevista termina aquí</h1>
        <div className="notice notice--warning" style={{ marginTop: 16 }}>
          <span className="notice__title">Motivo</span>
          {termination.message}
        </div>
        <p className="muted">
          Se registrará la disposición <span className="strong">{termination.disposition}</span> de
          esta vivienda. El registro cuenta para la tasa de respuesta, así que es importante
          guardarlo.
        </p>
      </main>
      <div className="actionbar">
        <button type="button" className="btn btn--primary btn--block" onClick={onConfirm} disabled={busy}>
          {busy ? 'Guardando…' : 'Registrar y continuar el recorrido'}
        </button>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*                                Auxiliares                                  */
/* -------------------------------------------------------------------------- */

/**
 * ¿El entrevistado NO conocía al personaje de su celda del experimento?
 *
 * Importa para el denominador: §Bloque 8 compara celdas entre sí, y una celda
 * cuyo personaje el entrevistado no conoce no es "no respondió", es "no
 * elegible". Confundirlas sesgaría el efecto de etiqueta.
 */
function isExperimentCellIneligible(draft: DraftInterview): boolean {
  if (!draft.experimentCell) return false;
  const i1 = draft.answers[`I1::${draft.experimentCell.personajeId}`];
  return !i1?.optionCode || i1.optionCode === 'no_conoce';
}

async function buildHouseholdPayload(draft: DraftInterview, terminatedAs?: string) {
  const household = draft.householdClientUuid
    ? await db.households.get(draft.householdClientUuid)
    : null;
  if (!household) return null;

  const disposition = terminatedAs ?? 'entrevista_completa';
  const eligibleAdults = draft.answers['F2']?.numericValue ?? household.eligibleAdults;

  return {
    clientUuid: household.clientUuid,
    clusterId: household.clusterId,
    assignmentId: household.assignmentId,
    sequenceNumber: household.sequenceNumber,
    manzanaCvegeo: household.manzanaCvegeo,
    addressNote: household.addressNote,
    latitude: household.latitude,
    longitude: household.longitude,
    accuracyMeters: household.accuracyMeters,
    disposition,
    eligibleAdults: eligibleAdults ?? null,
    visits: household.visits.map((visit, i) =>
      i === household.visits.length - 1 ? { ...visit, disposition } : visit,
    ),
  };
}

/** Avisa si el ritmo se desvía de la duración de diseño (§2: 18–20 min). */
function paceClass(elapsed: number, design: { min: number; max: number }): string {
  if (elapsed > design.max * 1.75) return 'badge badge--warn';
  // Por debajo de 10 minutos §9.4 manda auditar la entrevista.
  if (elapsed < 10) return 'faint';
  return 'badge badge--ok';
}

/** Bloques 3 y 9 usan la tarjeta de nombres del catálogo. */
function needsCardReminder(step: Step): boolean {
  if (step.block.id === 3 && step.question.code === 'I1') return true;
  if (step.block.id === 9 && step.row?.code === 'V1') return true;
  return false;
}
